import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

export type ThemeMode = 'light' | 'dark';

export interface ThemeTokens {
  colors: {
    background: string;
    backgroundSecondary: string;
    cardBackground: string;
    cardBorder: string;
    
    textPrimary: string;
    textSecondary: string;
    textTertiary: string;
    
    primary: string;
    primaryHover: string;
    danger: string;
    dangerHover: string;
    success: string;
    successHover: string;
    accent: string; // Added for compatibility
    
    headerBackground: string;
    headerText: string;
    
    inputBackground: string;
    inputBorder: string;
    inputText: string;
    
    statusGreen: string;
    statusAmber: string;
    statusRed: string;
    statusBlue: string;
    statusGrey: string;
    
    border: string;
    shadow: string;
    overlay: string;
  };
  
  spacing: {
    cardPadding: string;
    gridGap: string;
    sectionGap: string; // Added for compatibility
    itemGap: string; // Added for compatibility
  };
  
  effects: {
    cardShadow: string;
    borderRadius: string;
  };
  
  typography: {
    sectionTitle: { fontSize: string; fontWeight: number; }; // Added for compatibility
    bodyText: { fontSize: string; }; // Added for compatibility
    emptyState: { fontSize: string; }; // Added for compatibility
    pageTitle: { fontSize: string; }; // Added for compatibility
    fontSize: {
      small: string;
      base: string;
      large: string;
      title: string;
    };
  };
  
  layout: {
    maxWidth: string; // Added for compatibility
  };
}

interface ThemeContextType {
  theme: ThemeMode;
  setTheme: (mode: ThemeMode) => void;
  tokens: ThemeTokens;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'appThemeMode';

const lightTokens: ThemeTokens = {
  colors: {
    background: '#f5f7fa',
    backgroundSecondary: '#ffffff',
    cardBackground: '#ffffff',
    cardBorder: '#e2e8f0',
    
    textPrimary: '#1a202c',
    textSecondary: '#4a5568',
    textTertiary: '#718096',
    
    primary: '#3b82f6',
    primaryHover: '#2563eb',
    danger: '#ef4444',
    dangerHover: '#dc2626',
    success: '#10b981',
    successHover: '#059669',
    accent: '#3b82f6',
    
    headerBackground: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
    headerText: '#ffffff',
    
    inputBackground: '#ffffff',
    inputBorder: '#d1d5db',
    inputText: '#1a202c',
    
    statusGreen: '#10b981',
    statusAmber: '#f59e0b',
    statusRed: '#ef4444',
    statusBlue: '#3b82f6',
    statusGrey: '#6b7280',
    
    border: '#e2e8f0',
    shadow: 'rgba(0, 0, 0, 0.1)',
    overlay: 'rgba(0, 0, 0, 0.5)',
  },
  spacing: {
    cardPadding: '20px',
    gridGap: '20px',
    sectionGap: '20px',
    itemGap: '12px',
  },
  effects: {
    cardShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    borderRadius: '8px',
  },
  typography: {
    sectionTitle: { fontSize: '24px', fontWeight: 600 },
    bodyText: { fontSize: '17px' },
    emptyState: { fontSize: '17px' },
    pageTitle: { fontSize: '30px' },
    fontSize: {
      small: '12px',
      base: '14px',
      large: '16px',
      title: '20px',
    },
  },
  layout: {
    maxWidth: '1800px',
  },
};

const darkTokens: ThemeTokens = {
  colors: {
    background: '#0d1117',
    backgroundSecondary: '#161b22',
    cardBackground: '#161b22',
    cardBorder: '#30363d',
    
    textPrimary: '#e6edf3',
    textSecondary: '#8b949e',
    textTertiary: '#6e7681',
    
    primary: '#58a6ff',
    primaryHover: '#79c0ff',
    danger: '#f85149',
    dangerHover: '#ff7b72',
    success: '#3fb950',
    successHover: '#56d364',
    accent: '#58a6ff',
    
    headerBackground: 'linear-gradient(135deg, #0d1117 0%, #161b22 100%)',
    headerText: '#e6edf3',
    
    inputBackground: '#0d1117',
    inputBorder: '#30363d',
    inputText: '#e6edf3',
    
    statusGreen: '#3fb950',
    statusAmber: '#d29922',
    statusRed: '#f85149',
    statusBlue: '#58a6ff',
    statusGrey: '#8b949e',
    
    border: '#30363d',
    shadow: 'rgba(0, 0, 0, 0.5)',
    overlay: 'rgba(0, 0, 0, 0.7)',
  },
  spacing: {
    cardPadding: '20px',
    gridGap: '20px',
    sectionGap: '20px',
    itemGap: '12px',
  },
  effects: {
    cardShadow: '0 0 0 1px #30363d',
    borderRadius: '8px',
  },
  typography: {
    sectionTitle: { fontSize: '24px', fontWeight: 600 },
    bodyText: { fontSize: '17px' },
    emptyState: { fontSize: '17px' },
    pageTitle: { fontSize: '30px' },
    fontSize: {
      small: '12px',
      base: '14px',
      large: '16px',
      title: '20px',
    },
  },
  layout: {
    maxWidth: '1800px',
  },
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === 'dark' ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  });

  const tokens = theme === 'light' ? lightTokens : darkTokens;

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
      const t = theme === 'light' ? lightTokens : darkTokens;
      document.body.style.backgroundColor = t.colors.background;
      document.body.style.color = t.colors.textPrimary;
    } catch (error) {
      console.error('Error saving theme:', error);
    }
  }, [theme]);

  const setTheme = (mode: ThemeMode) => {
    setThemeState(mode);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, tokens }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
